import { sameLines } from './shippingInstructions';
import type {
  DraftPurchaseOrderFreight,
  FreightOptionRow,
  ListFreightCodesResponse,
  ReleaseFreightChoice,
  SetDraftFreightInput,
} from '@erp/rpc';

type Code = ListFreightCodesResponse['shipVia'][number];

/** "PL · PUROLATOR", or the bare code when the division has no description. */
export function describeCode(
  code: string | null,
  codes: readonly Code[],
): string {
  if (!code) return '';
  const description = codes.find((entry) => entry.code === code)?.description;
  return description ? `${code} · ${description}` : code;
}

/** Who pays under the draft's freight terms, from the division's codes. */
export function termsKindOf(
  freightTermsCode: string | null,
  codes: readonly Code[],
): Code['termsKind'] {
  if (!freightTermsCode) return null;
  return (
    codes.find((entry) => entry.code === freightTermsCode)?.termsKind ?? null
  );
}

/** The options a buyer may pick for this draft: confirmed, for any vendor or this one. */
export function pickableOptions(
  options: readonly FreightOptionRow[],
  supplierId: string,
): FreightOptionRow[] {
  return options.filter(
    (option) =>
      option.status === 'confirmed' &&
      (option.supplierId === null || option.supplierId === supplierId),
  );
}

/**
 * What picking an option starts from: its carrier account (a collect option
 * fills in the branch's account) and its default shipping instructions.
 */
export function pickedOptionStart(
  options: readonly PickableOption[],
  id: string,
): { carrierAccount: string; instructions: string[] } {
  const option = options.find((row) => row.id === id);
  return {
    carrierAccount: option?.carrierAccount ?? '',
    instructions: option?.instructions ?? [],
  };
}

type PickableOption = Pick<
  FreightOptionRow,
  'id' | 'carrierAccount' | 'instructions'
>;

/**
 * The picked option, plus the carrier account and shipping instructions only
 * where they differ from the option's own, so keeping a suggestion untouched
 * still counts as keeping it.
 */
function pickedFreight(
  option: PickableOption,
  carrierAccount: string,
  instructions: string[],
): ReleaseFreightChoice {
  const typed = carrierAccount.trim();
  const changed = typed !== (option.carrierAccount ?? '');
  return {
    freightOptionId: option.id,
    ...(changed && { carrierAccount: typed || null }),
    ...(!sameLines(instructions, option.instructions) && { instructions }),
  };
}

/** What saving the picker sends. */
export function draftFreightSaveInput(params: {
  freight: Pick<DraftPurchaseOrderFreight, 'draftPurchaseOrderId'>;
  option: PickableOption;
  carrierAccount: string;
  instructions: string[];
}): SetDraftFreightInput {
  return {
    draftPurchaseOrderId: params.freight.draftPurchaseOrderId,
    ...pickedFreight(params.option, params.carrierAccount, params.instructions),
  };
}

/**
 * What releasing sends for freight: nothing while the buyer keeps the
 * preselected option, account and shipping instructions (the server then
 * applies its own suggestion, or an open PO keeps its freight), else the
 * picked option.
 */
export function releaseFreightChoice(params: {
  option: PickableOption | undefined;
  carrierAccount: string;
  instructions: string[];
  preselected: {
    optionId: string | null;
    carrierAccount: string | null;
    instructions: string[];
  };
}): ReleaseFreightChoice | undefined {
  const { option, preselected } = params;
  if (!option) return undefined;
  const kept =
    option.id === preselected.optionId &&
    params.carrierAccount.trim() === (preselected.carrierAccount ?? '') &&
    sameLines(params.instructions, preselected.instructions);
  return kept
    ? undefined
    : pickedFreight(option, params.carrierAccount, params.instructions);
}
